import React from 'react';
import loadable from '@loadable/component';
import { Button, Input, Message, Dimmer, Loader } from 'semantic-ui-react';
import { readAsDataURL } from 'promise-file-reader';
import { useDispatch } from 'react-redux';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import { createContent } from '@plone/volto/actions';
import { getBaseUrl, isInternalURL } from '@plone/volto/helpers';
import { Icon } from '@plone/volto/components';
import { toast } from 'react-toastify';
import Toast from '@plone/volto/components/manage/Toast/Toast';

import aheadSVG from '@plone/volto/icons/ahead.svg';
import uploadSVG from '@plone/volto/icons/upload.svg';
import navTreeSVG from '@plone/volto/icons/nav.svg';
import clearSVG from '@plone/volto/icons/clear.svg';

const Dropzone = loadable(() => import('react-dropzone'));

const messages = defineMessages({
  ImageBlockInputPlaceholder: {
    id: 'Browse the site, drop a PDF document or type an URL',
    defaultMessage: 'Browse the site, drop a PDF document or type an URL',
  },
  Origin: {
    id: 'Origin',
    defaultMessage: 'Origin',
  },
  externalURL: {
    id: 'External URL',
    defaultMessage: 'External URL',
  },
  Error: {
    id: 'Error',
    defaultMessage: 'Error',
  },
  IncorrectUrl: {
    id: 'Only Internal url is allowed.',
    defaultMessage: 'Only Internal url is allowed.',
  },
});

export function usePrevious(value) {
  const ref = React.useRef();
  React.useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
}

const UploadWidget = ({
  value,
  onChange,
  onFocus,
  icon,
  pathname,
  openObjectBrowser,
  block,
  id,
  requestId,
}) => {
  const [localValue, setLocalValue] = React.useState(value);
  const [isUploading, setIsUploading] = React.useState(false);
  const [isDragging, setIsDragging] = React.useState(false);
  const intl = useIntl();

  const oldValue = usePrevious(value);

  React.useEffect(() => {
    if (value !== oldValue) setLocalValue(oldValue);
  }, [value, oldValue]);

  const dispatch = useDispatch();

  const uploadFileData = React.useCallback(
    ({ filename, data }) => {
      const fields = data.match(/^data:(.*);(.*),(.*)$/);
      const action = createContent(
        getBaseUrl(pathname),
        {
          '@type': 'File',
          filename,
          file: {
            data: fields[3],
            encoding: fields[2],
            'content-type': fields[1],
            filename,
          },
        },
        block,
      );
      dispatch(action);
    },
    [dispatch, pathname, block],
  );

  const onDropFile = (file) => {
    setIsUploading(true);

    readAsDataURL(file[0]).then((data) => {
      uploadFileData({ filename: file[0].name, data });
    });
  };

  const onUploadFile = React.useCallback(
    (event) => {
      event.stopPropagation();
      event.preventDefault();

      const { target } = event;
      const file = target.files[0];
      setIsUploading(true);

      readAsDataURL(file).then((data) => {
        uploadFileData({ filename: file.name, data });
      });
    },
    [uploadFileData],
  );

  const onSubmit = React.useCallback(
    (id, localValue) => {
      if (!isInternalURL(localValue)) {
        toast.error(
          <Toast
            error
            title={intl.formatMessage(messages.Error)}
            content={intl.formatMessage(messages.IncorrectUrl)}
          />,
        );

        return;
      }

      onChange(id, localValue);
    },
    [intl, onChange],
  );

  const onKeyDownVariantMenuForm = React.useCallback(
    (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        onSubmit(id, localValue);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        // TODO: Do something on ESC key
      }
    },
    [onSubmit, id, localValue],
  );

  return (
    <div className="upload-widget">
      <Dropzone
        noClick
        onDrop={onDropFile}
        onDragEnter={() => setIsDragging(true)}
        onDragLeave={() => setIsDragging(false)}
        className="dropzone"
      >
        {({ getRootProps, getInputProps }) => (
          <div {...getRootProps()}>
            <Message>
              {isDragging && <Dimmer active></Dimmer>}
              {isUploading && (
                <Dimmer active>
                  <Loader indeterminate>Uploading image</Loader>
                </Dimmer>
              )}
              <center>
                <img src={icon} alt="" />
                <div className="toolbar-inner">
                  <Button.Group>
                    <Button
                      basic
                      icon
                      onClick={(e) => {
                        e.stopPropagation();
                        openObjectBrowser({
                          mode: 'link',
                          onSelectItem: (url) => onChange(id, url),
                        });
                      }}
                    >
                      <Icon name={navTreeSVG} size="24px" />
                    </Button>
                  </Button.Group>
                  <Button.Group>
                    <label className="ui button basic icon">
                      <Icon name={uploadSVG} size="24px" />
                      <input
                        {...getInputProps({
                          type: 'file',
                          onChange: onUploadFile,
                          style: { display: 'none' },
                        })}
                      />
                    </label>
                  </Button.Group>
                  <Input
                    onKeyDown={onKeyDownVariantMenuForm}
                    onChange={(event, data) => {
                      setLocalValue(data.value);
                    }}
                    value={localValue}
                    placeholder={intl.formatMessage(
                      messages.ImageBlockInputPlaceholder,
                    )}
                    onClick={(e) => {
                      e.target.focus();
                    }}
                    onFocus={onFocus}
                    style={{ width: '100%' }}
                  />
                  {localValue && (
                    <Button.Group>
                      <Button
                        basic
                        className="cancel"
                        onClick={(e) => {
                          e.stopPropagation();
                          setLocalValue('');
                          onChange(id, '');
                        }}
                      >
                        <Icon name={clearSVG} size="30px" />
                      </Button>
                    </Button.Group>
                  )}
                  <Button.Group>
                    <Button
                      basic
                      primary
                      disabled={!localValue}
                      onClick={(e) => {
                        e.stopPropagation();
                        onSubmit(id, localValue);
                      }}
                    >
                      <Icon name={aheadSVG} size="30px" />
                    </Button>
                  </Button.Group>
                </div>
                <div className="message-text">
                  <FormattedMessage
                    id="Please remove all @'s in pdf direct download url."
                    defaultMessage="Please remove all @'s in pdf direct download url."
                  />
                </div>
              </center>
            </Message>
          </div>
        )}
      </Dropzone>
    </div>
  );
};

export default UploadWidget;
